import React, { useCallback } from "react";
import styles from "./SharingScreenLayout.module.css";
import { useDispatch, useSelector } from "react-redux";
import { getPublicationTracks } from "utils";
import { isMobile } from "react-device-detect";
import { Room } from "twilio-video";
import { toggleScreenShare } from "store/local-tracks/slice";
import { RootState } from "store/reducers";
import { RemoteParticipant } from "../remote-participant/RemoteParticipant";
import { LocalParticipant } from "../local-participant/LocalParticipant";
import { useParticipantsInAreaFilter } from "hooks/useParticipantsInAreaFilter";
import { useInteractivityListeners } from "hooks/useInteractivityListeners";
import { ChatBox } from "../chat/ChatBox";
import { ShareScreenBtnGroup } from "../../shared/btn-group/ShareScreenBtnGroup";

interface SharingScreenLayoutProps {
  room: Room;
  participantsInArea: string[];
}

/** This layout component is rendered for a participant when they are screen sharing. */
export const SharingScreenLayout: React.FC<SharingScreenLayoutProps> = ({
  room,
  participantsInArea,
}) => {
  const dispatch = useDispatch();
  const { participants } = useSelector((s: RootState) => s.remoteParticipants);
  const { participantsInAreaFilter } = useParticipantsInAreaFilter(
    participantsInArea
  );
  const { showChat } = useSelector((s: RootState) => s.chatMessages);
  const { privateChatEnabled } = useSelector((s: RootState) => s.user);
  useInteractivityListeners(styles["presentation-container"]);

  const stopSharing = useCallback(() => {
    if (room) {
      const screenShareTrack = getPublicationTracks(
        room.localParticipant.videoTracks
      ).find((t) => /-screen/g.test(t.name));

      if (screenShareTrack) {
        screenShareTrack.stop();
        dispatch(toggleScreenShare(false));
      }
    }
  }, [dispatch, room]);

  return (
    <>
      <div className={styles["presentation-container"]}>
        <div className={styles["program-feed-container"]}>
          <div className={styles["presentation-dialog"]}>
            <p className="lead text-center">
              You are sharing your screen with everyone.
            </p>
            <button className="btn btn-primary" onClick={stopSharing}>
              Stop sharing
            </button>
          </div>
        </div>
        <div
          className={`${styles["remote-participants-sidebar"]} ${
            isMobile ? styles["hide"] : ""
          }`}
        >
          {!isMobile && (
            <LocalParticipant
              key={room.localParticipant.sid + "-local-participant"}
              participant={room.localParticipant}
              inSidebar={true}
            />
          )}
          {privateChatEnabled && <ShareScreenBtnGroup />}
          <span className={`${styles["participant-wrapper"]} ${showChat ? "" : styles["show"]}`}>
          {participants.filter(participantsInAreaFilter).map((p) => {
            const participant = room.participants.get(p.sid);
            if (participant) {
              return (
                <RemoteParticipant
                  key={participant.sid + "-remote-tracks"}
                  participant={participant}
                  participantState={p}
                  inSidebar={true}
                  audioOnly={true}
                  isDominantSpeaker={
                    room?.dominantSpeaker?.sid === participant.sid
                  }
                />
              );
            }
            return null;
          })}
          </span>
          {privateChatEnabled && 
            <span className={`${styles["chat-wrapper"]} ${showChat ? styles["show"] : ""}`}>
              <ChatBox
                isShareLayout={true}
              />
            </span>
          }
        </div>
      </div>
    </>
  );
};
